import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'

import { useUpdatePackageReq } from '../../../api/package-req/mutations/useUpdatePackageReq'
import editIcon from '../../../imgs/edit.png'
import PackageContents from '../PackageContents'
const twoDecimals = num => {
  return Math.floor(num * 100) / 100
}
const inchToCm = num => {
  return twoDecimals(Math.floor((num || 0) * 2.54 * 100) / 100)
}
const cmToInch = num => {
  return Math.floor(((num || 0) / 2.54) * 100) / 100
}
const kgToPound = num => {
  return Math.floor(((num || 0) / 0.4535923) * 100) / 100
}
const poundToKg = num => {
  return Math.floor((num || 0) * 0.4535923 * 100) / 100
}
const isNotNumber = num => {
  return isNaN(parseFloat(num))
}
export function UpdatePackageModal({ item, updatePackage, pkgReqs }) {
  const [show, setShow] = useState(false)
  const { loggedUser } = useSelector(state => state.user)
  const { mutateAsync: updatePackageReq } = useUpdatePackageReq()
  const [currPackage, setCurrPackage] = useState({ ...item })
  const { warehouse } = item

  const [height, setHeight] = useState(warehouse === 'USA' ? cmToInch(item.height) : item.height)
  const [width, setWidth] = useState(warehouse === 'USA' ? cmToInch(item.width) : item.width)
  const [length, setLength] = useState(warehouse === 'USA' ? cmToInch(item.length) : item.length)
  const [weight, setWeight] = useState(warehouse === 'USA' ? kgToPound(item.weight) : item.weight)
  const [uniteFee, setUniteFee] = useState(0)
  const [photoRequestFeeUSD, setPhotoRequestFeeUSD] = useState(0)
  const [shrinkRequestFeeUSD, setShrinkRequestFeeUSD] = useState(0)

  const pkgReqsForPackage = useMemo(() => {
    const unitedReq = pkgReqs.find(req => req.reqType === 'uniteRequest' && req.unitedPackageId === item.id)
    if (!unitedReq) return pkgReqs.filter(req => req.packageId === item.id)
    const pkgIds = [item.id, ...unitedReq.unitePackageIds]
    return pkgReqs.filter(req => pkgIds.includes(req.packageId))
  }, [pkgReqs, item.id])

  const activeUniteReqs = useMemo(
    () =>
      pkgReqsForPackage?.filter(
        req =>
          req.reqType === 'uniteRequest' && (req.statusUpdates?.[0]?.status === 'pending' || req.statusUpdates?.[0]?.status === 'approved')
      ),
    [pkgReqsForPackage]
  )
  const activePhotoReqs = useMemo(
    () =>
      pkgReqsForPackage?.filter(
        req =>
          req.reqType === 'photoRequest' && (req.statusUpdates?.[0]?.status === 'pending' || req.statusUpdates?.[0]?.status === 'approved')
      ),
    [pkgReqsForPackage]
  )
  const activeShrinkReqs = useMemo(
    () =>
      pkgReqsForPackage?.filter(
        req =>
          req.reqType === 'shrinkRequest' && (req.statusUpdates?.[0]?.status === 'pending' || req.statusUpdates?.[0]?.status === 'approved')
      ),
    [pkgReqsForPackage]
  )
  const confirmBtnHandler = async () => {
    // check if value is 0.1 or less, set to 0.1
    const validHeight = parseFloat(height)
    const validWidth = parseFloat(width)
    const validLength = parseFloat(length)
    const validWeight = parseFloat(weight)
    if (isNotNumber(validHeight) || isNotNumber(validWidth) || isNotNumber(validLength) || isNotNumber(validWeight)) {
      alert('Please insert valid package dimensions')
      return
    }
    const newDimensions = {
      height: warehouse === 'USA' ? inchToCm(validHeight) : validHeight,
      width: warehouse === 'USA' ? inchToCm(validWidth) : validWidth,
      length: warehouse === 'USA' ? inchToCm(validLength) : validLength,
      weight: warehouse === 'USA' ? poundToKg(validWeight) : validWeight,
    }
    if (parseFloat(newDimensions.weight) < 0.1) newDimensions.weight = 0.1
    if (parseFloat(newDimensions.height) < 0.1) newDimensions.height = 0.1
    if (parseFloat(newDimensions.width) < 0.1) newDimensions.width = 0.1
    if (parseFloat(newDimensions.length) < 0.1) newDimensions.length = 0.1

    try {
      // Update package without fees
      await updatePackage({ ...currPackage, ...newDimensions })

      // Update fees in package requests
      await updatePackageRequestFees()

      setShow(false)
    } catch (error) {
      console.log(error)
      alert(`error! ${error.response.data.message}`)
    }
  }

  const cancelBtnHandler = () => {
    setShow(false)
  }

  const onPackageContentAdded = content => {
    setCurrPackage({
      ...currPackage,
      contents: [...(currPackage.contents || []), content],
    })
  }

  const onPackageContentRemoved = index => {
    setCurrPackage({
      ...currPackage,
      contents: currPackage.contents.filter((v, i) => i !== index),
    })
  }

  // Fetch package requests when modal opens
  useEffect(() => {
    if (show && item.id) {
      fetchPackageRequests()
    }
  }, [show, item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPackageRequests = async () => {
    try {
      setUniteFee(activeUniteReqs?.reduce((acc, req) => acc + req.feeUSD, 0) || 0)
      setPhotoRequestFeeUSD(activePhotoReqs?.reduce((acc, req) => acc + req.feeUSD, 0) || 0)
      setShrinkRequestFeeUSD(activeShrinkReqs?.reduce((acc, req) => acc + req.feeUSD, 0) || 0)
    } catch (error) {
      console.error('Error fetching package requests:', error)
    }
  }

  const updatePackageRequestFees = async () => {
    try {
      // Update each fee in its respective package request if it exists
      const updateItems = []

      if (activeUniteReqs?.length && uniteFee !== activeUniteReqs.reduce((acc, req) => acc + req.feeUSD, 0)) {
        const feeUsdSplit = uniteFee / activeUniteReqs.length
        activeUniteReqs.forEach(req => {
          updateItems.push({
            packageReqId: req.id,
            feeUSD: feeUsdSplit,
          })
        })
      }
      if (activePhotoReqs?.length && photoRequestFeeUSD !== activePhotoReqs.reduce((acc, req) => acc + req.feeUSD, 0)) {
        const feeUsdSplit = photoRequestFeeUSD / activePhotoReqs.length
        activePhotoReqs.forEach(req => {
          updateItems.push({
            packageReqId: req.id,
            feeUSD: feeUsdSplit,
          })
        })
      }

      if (activeShrinkReqs?.length && shrinkRequestFeeUSD !== activeShrinkReqs.reduce((acc, req) => acc + req.feeUSD, 0)) {
        const feeUsdSplit = shrinkRequestFeeUSD / activeShrinkReqs.length
        activeShrinkReqs.forEach(req => {
          updateItems.push({ packageReqId: req.id, feeUSD: feeUsdSplit })
        })
      }

      await Promise.all(updateItems.map(updatePackageReq))
    } catch (error) {
      console.error('Error updating package request fees:', error)
      throw error
    }
  }

  return (
    <div>
      <img src={editIcon} onClick={() => setShow(true)} height='30px' style={{ cursor: 'pointer' }} />

      <Modal size='xl' show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <div>
            <Form style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Form.Group controlId='exampleForm.ControlInput1' style={{ flexGrow: '1' }}>
                  <Form.Label>Brand</Form.Label>
                  <Form.Control
                    onChange={e => {
                      setCurrPackage({ ...currPackage, brand: e.target.value })
                    }}
                    name='brand'
                    type='text'
                    value={currPackage.brand}
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput2'>
                  <Form.Label>Height {warehouse === 'USA' ? '(INCH)' : '(CM)'}</Form.Label>
                  <Form.Control
                    onChange={e => setHeight(e.target.value)}
                    name='height'
                    type='number'
                    step='any'
                    value={height}
                    onWheel={e => e.target.blur()} // Prevent scroll from changing the value
                  />

                  {warehouse === 'USA' && (
                    <>
                      <Form.Label>Height (CM)</Form.Label>
                      <Form.Control name='height' type='number' step='any' value={inchToCm(height)} disabled />
                    </>
                  )}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput3'>
                  <Form.Label>Width {warehouse === 'USA' ? '(INCH)' : '(CM)'}</Form.Label>
                  <Form.Control
                    onChange={e => setWidth(e.target.value)}
                    name='width'
                    type='number'
                    step='any'
                    value={width}
                    onWheel={e => e.target.blur()} // Prevent scroll from changing the value
                  />
                  {warehouse === 'USA' && (
                    <>
                      <Form.Label>Width (CM)</Form.Label>
                      <Form.Control name='width' type='number' step='any' value={inchToCm(width)} disabled />
                    </>
                  )}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput4'>
                  <Form.Label>Length {warehouse === 'USA' ? '(INCH)' : '(CM)'}</Form.Label>
                  <Form.Control
                    onChange={e => setLength(e.target.value)}
                    name='length'
                    type='number'
                    step='any'
                    value={length}
                    onWheel={e => e.target.blur()} // Prevent scroll from changing the value
                  />

                  {warehouse === 'USA' && (
                    <>
                      <Form.Label>Length (CM)</Form.Label>
                      <Form.Control name='length' type='number' step='any' value={inchToCm(length)} disabled />
                    </>
                  )}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput5'>
                  <Form.Label>Weight {warehouse === 'USA' ? '(Pound)' : '(KG)'}</Form.Label>
                  <Form.Control
                    onChange={e => setWeight(e.target.value)}
                    name='weight'
                    type='number'
                    step='any'
                    value={weight}
                    onWheel={e => e.target.blur()} // Prevent scroll from changing the value
                  />

                  {warehouse === 'USA' && (
                    <>
                      <Form.Label>Weight (KG)</Form.Label>
                      <Form.Control name='weight' type='number' step='any' value={poundToKg(weight)} disabled />
                    </>
                  )}
                </Form.Group>
              </div>

              <PackageContents
                onContentAdded={onPackageContentAdded}
                onContentsRemoved={onPackageContentRemoved}
                packageState={currPackage}
                setPackageState={setCurrPackage}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                <Form.Group controlId='exampleForm.ControlInput7' style={{ marginRight: '1rem' }}>
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    name='price'
                    onChange={e => {
                      const value = parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : 0
                      setCurrPackage({
                        ...currPackage,
                        price: value,
                        valueInUSD: value,
                      })
                    }}
                    type='number'
                    value={currPackage.price}
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput8' style={{ marginRight: '1rem' }}>
                  <Form.Label>trackingNumberInTurkey</Form.Label>
                  <Form.Control
                    name='trackingNumberInTurkey'
                    onChange={e => {
                      setCurrPackage({
                        ...currPackage,
                        trackingNumberInTurkey: e.target.value,
                      })
                    }}
                    type='text'
                    value={currPackage.trackingNumberInTurkey}
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput9' style={{ marginRight: '1rem' }}>
                  <Form.Label>trackingNumberToIsrael</Form.Label>
                  <Form.Control
                    onChange={e => {
                      setCurrPackage({
                        ...currPackage,
                        trackingNumberToIsrael: e.target.value,
                      })
                    }}
                    name='trackingNumberToIsrael'
                    type='text'
                    value={currPackage.trackingNumberToIsrael}
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>Delay Fee</Form.Label>
                  <Form.Control
                    name='delayChargePrice'
                    onChange={e => {
                      setCurrPackage({
                        ...currPackage,
                        delayChargePrice: parseFloat(e.target.value),
                      })
                    }}
                    type='text'
                    value={currPackage.delayChargePrice ?? 0}
                    disabled={
                      loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Agent']
                    }
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>delayStatus</Form.Label>
                  <Form.Control
                    name='delayStatus'
                    onChange={e => {
                      setCurrPackage({
                        ...currPackage,
                        delayStatus: e.target.value,
                      })
                    }}
                    type='text'
                    value={currPackage.delayStatus ?? 0}
                    disabled={
                      loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Agent']
                    }
                  />
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>Unite Fee</Form.Label>
                  <Form.Control
                    name='uniteFee'
                    onChange={e => {
                      setUniteFee(parseFloat(e.target.value) || 0)
                    }}
                    type='text'
                    value={uniteFee}
                    disabled={
                      loggedUser.permissions['Worker'] ||
                      loggedUser.permissions['OfficeManager'] ||
                      loggedUser.permissions['Agent'] ||
                      !activeUniteReqs
                    }
                  />
                  {!activeUniteReqs && <small className='text-muted'>No active unite request</small>}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>Photo Fee</Form.Label>
                  <Form.Control
                    name='photoRequestFeeUSD'
                    onChange={e => {
                      setPhotoRequestFeeUSD(parseFloat(e.target.value) || 0)
                    }}
                    type='text'
                    value={photoRequestFeeUSD}
                    disabled={
                      loggedUser.permissions['Worker'] ||
                      loggedUser.permissions['OfficeManager'] ||
                      loggedUser.permissions['Agent'] ||
                      !activePhotoReqs?.length
                    }
                  />
                  {!activePhotoReqs?.length && <small className='text-muted'>No active photo request</small>}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>Shrink Fee</Form.Label>
                  <Form.Control
                    name='shrinkRequestFeeUSD'
                    onChange={e => {
                      setShrinkRequestFeeUSD(parseFloat(e.target.value) || 0)
                    }}
                    type='text'
                    value={shrinkRequestFeeUSD}
                    disabled={
                      loggedUser.permissions['Worker'] ||
                      loggedUser.permissions['OfficeManager'] ||
                      loggedUser.permissions['Agent'] ||
                      !activeShrinkReqs?.length
                    }
                  />
                  {!activeShrinkReqs?.length && <small className='text-muted'>No active shrink request</small>}
                </Form.Group>

                <Form.Group controlId='exampleForm.ControlInput10' style={{ marginRight: '1rem' }}>
                  <Form.Label>Warehouse</Form.Label>
                  <Form.Control
                    name='Warehouse'
                    onChange={e => {
                      setCurrPackage({
                        ...currPackage,
                        warehouse: e.target.value,
                      })
                    }}
                    type='text'
                    value={currPackage.warehouse ?? ''}
                    disabled={
                      loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Agent']
                    }
                  />
                </Form.Group>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
                <Form.Group>
                  <Form.Label>
                    <u>Is Shipped?</u>
                  </Form.Label>
                  <Form.Check
                    checked={currPackage.isShipped && true}
                    onChange={() => {
                      loggedUser.permissions['Admin'] && setCurrPackage({ ...currPackage, isShipped: true })
                    }}
                    type='radio'
                    label='Yes'
                    name='formHorizontalRadios1'
                    id='formHorizontalRadios1'
                  />
                  <Form.Check
                    checked={!currPackage.isShipped && true}
                    onChange={() => {
                      loggedUser.permissions['Admin'] && setCurrPackage({ ...currPackage, isShipped: false })
                    }}
                    type='radio'
                    label='No'
                    name='formHorizontalRadios2'
                    id='formHorizontalRadios2'
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    <u>Is Paid?</u>
                  </Form.Label>
                  <Form.Check
                    checked={currPackage.isPaid}
                    onChange={() => {
                      setCurrPackage({
                        ...currPackage,
                        isPaid: true,
                      })
                    }}
                    type='radio'
                    label='Yes'
                    name='formHorizontalRadios3'
                    id='formHorizontalRadios3'
                    disabled={!(loggedUser.permissions['Admin'] || loggedUser.permissions['SeniorAgent'])}
                  />
                  <Form.Check
                    checked={!currPackage.isPaid}
                    onChange={() => {
                      setCurrPackage({
                        ...currPackage,
                        isPaid: false,
                      })
                    }}
                    type='radio'
                    label='No'
                    name='formHorizontalRadios4'
                    id='formHorizontalRadios4'
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    <u>Shipping Method</u>
                  </Form.Label>
                  <Form.Check
                    checked={currPackage.shippingMethod === 'doorToDoor'}
                    onChange={() => {
                      setCurrPackage({
                        ...currPackage,
                        shippingMethod: 'doorToDoor',
                      })
                    }}
                    type='radio'
                    label='Door To Door'
                    name='formHorizontalRadios5'
                    id='formHorizontalRadios5'
                    disabled={!loggedUser.permissions['Admin']}
                  />
                  <Form.Check
                    checked={currPackage.shippingMethod === 'pickUpPoint'}
                    onChange={() => {
                      setCurrPackage({
                        ...currPackage,
                        shippingMethod: 'pickUpPoint',
                      })
                    }}
                    type='radio'
                    label='Pick Up Point'
                    name='formHorizontalRadios6'
                    id='formHorizontalRadios6'
                    disabled={!loggedUser.permissions['Admin']}
                  />
                </Form.Group>
              </div>

              <Button onClick={confirmBtnHandler}>Confirm</Button>
              <Button variant='info' onClick={cancelBtnHandler}>
                Cancel
              </Button>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
